import React, { useEffect, useRef } from 'react'

import { useLocation } from 'react-router'
import { NavLink, useParams } from 'react-router-dom'

import { NewsBlock, NewsQuoteBlock, NewsTextBlock } from 'api/models'

import { useWrapperContext } from 'components/Wrapper/Wrapper'
import { PartnersForm } from 'components/Partners'
import { BackButton, Empty, Separator, Skeleton } from 'shared/components'
import { SocialShare } from './SocialShare'

import {
  useActions,
  useSticky,
  useTypedSelector,
  useWindowSize,
} from 'shared/hooks'
import { dateToLocaleString } from 'shared/utils'

import classNames from 'classnames'
import classes from './NewsItem.module.scss'

export const NewsItem = (props: { isDraft?: boolean }): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null)
  const [transform] = useSticky({ ref })
  const { setHeaderWhite } = useWrapperContext()
  const { item, error, loading } = useTypedSelector(state => state.news)
  const { fetchNewsItem } = useActions()
  const { id } = useParams()
  const { isDesktop, isLargeDesktop } = useWindowSize()
  const location = useLocation()

  useEffect(() => {
    setHeaderWhite(true)

    fetchNewsItem(id, props?.isDraft)
    return () => setHeaderWhite(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderText = (item?: NewsTextBlock) => {
    const render = (item?: NewsTextBlock, itemIndex = 0) => (
      <div
        className={classes.news__content_text}
        key={itemIndex}
        dangerouslySetInnerHTML={{ __html: item?.body || '' }}
      />
    )

    if (loading) {
      return <Skeleton className={classes.news__content_text} />
    }

    return item ? render(item) : null
  }

  const renderQuote = (item?: NewsQuoteBlock, itemIndex?: number) => {
    if (!item) {
      return
    }

    const author = item.author
    const authorPosition = author.position
    const authorCompany = author?.company?.length ? `, ${author.company}` : ''
    const authorInfo = `${authorPosition} ${authorCompany}`
    const photo = author?.image?.href
    const cn = classNames([
      classes.news__content_quote,
      { [classes.withBorder]: !photo },
    ])

    return (
      <div className={cn} key={itemIndex}>
        <div className={classes.news__content_quote_bg}></div>
        {photo && (
          <div className={classes.news__content_quote_img_container}>
            <div className={classes.news__content_quote_img}>
              <img src={photo} alt="" />
            </div>
            <div className={classes.news__content_quote_author}>
              <span>{author.name}</span> {authorInfo}
            </div>
          </div>
        )}
        <div>
          <span dangerouslySetInnerHTML={{ __html: item?.body }} />

          <div className={classes.news__content_quote_author}>
            — <span>{author.name}</span>, {authorInfo}
          </div>
        </div>
      </div>
    )
  }

  const renderSignature = () => {
    if (loading) {
      return <Skeleton className={classes.news__content_sign} />
    }

    return item ? (
      <div
        className={classes.news__content_sign}
        dangerouslySetInnerHTML={{ __html: item?.footerText || '' }}
      />
    ) : null
  }

  const renderContent = () => {
    const render = (item?: NewsTextBlock, itemIndex = 0) => {
      return (
        <div className={classes.news__content} key={itemIndex}>
          {item?.title?.length > 0 && (
            <h3 className={`${classes.news__content_title}`}>
              {loading ? <Skeleton /> : item?.title}
            </h3>
          )}
          {renderText(item)}
        </div>
      )
    }
    if (!item) {
      return render()
    }

    return item?.data.map((item: NewsBlock, itemIndex: number) => {
      if (item.type === 'text') {
        return render(item.text, itemIndex)
      }
      if (item.type === 'quote') {
        return renderQuote(item.quote, itemIndex)
      }
    })
  }

  const renderImage = () => {
    if (loading) {
      return <Skeleton height="300px" />
    }

    return item && item?.imgSquare ? (
      <img className={classes.news__image} src={item.imgSquare?.href} alt="" />
    ) : null
  }

  if (error) {
    return <Empty description={error} />
  }

  return (
    <div className={`${classes.wrapper} offset-header`}>
      <div className="container container-full">
        <NavLink to={(location.state as any)?.prevPath || '/news'}>
          <BackButton text="Назад" />
        </NavLink>
      </div>
      <div className="container">
        <div className="row">
          <div className="col-xs-12 col-lg-10 col-lg-offset-1 col-xl-12 col-xl-offset-0">
            <div className={classes.news}>
              <div className="row">
                <div className="col-xs-12 col-sm-10 col-sm-offset-1">
                  {/* Title */}
                  <h1 className={classes.news__title}>
                    {loading ? <Skeleton /> : item?.title}
                  </h1>
                  {/* Date */}
                  <span className={classes.news__date}>
                    {loading && <Skeleton />}
                    {item && !loading && dateToLocaleString(item.date, true)}
                  </span>
                </div>
              </div>

              {/* Image */}
              {renderImage()}
              <div className={`${classes['sticky-container']} row`} ref={ref}>
                <div
                  className={`${classes.sticky} col-xs-12 col-ms-offset-0 col-sm-offset-1 col-lg-1 col-lg-offset-1`}
                  style={{
                    transform: isLargeDesktop || isDesktop ? transform : '',
                  }}
                >
                  {item && (
                    <SocialShare
                      id={item.id}
                      title={item?.title}
                      image={item?.imgSquare?.href}
                    />
                  )}
                </div>
                <div className="col-xs-12 col-sm-10 col-xs-offset-0 col-sm-offset-1 col-lg-9 col-lg-offset-0">
                  {/* Content */}
                  {renderContent()}
                  {/* Sign */}
                  {renderSignature()}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Separator height={50} />
        <PartnersForm />
        <Separator height={50} />
      </div>
    </div>
  )
}
